type Pending = { resolve: (value: unknown) => void; reject: (error: Error) => void };

export const WORKER_URL = '/runtime-worker.js';

/** `rsbuild.config.ts` 와 운영 웹서버 설정이 글자까지 같아야 한다. */
export const WORKER_CSP =
  "default-src 'none'; script-src 'self' 'unsafe-eval' 'wasm-unsafe-eval'; " +
  "connect-src 'self'; worker-src 'none'";

// 무엇이 실제로 막는지 잰 것(2026-08-06, Edge):
//   connect-src 'self' → 바깥 fetch 차단   script-src 'self' → 바깥 import() 차단
//   worker-src 'none'  → 중첩 워커 차단     같은 출처 fetch 는 살아 있다(Pyodide 가 쓴다)
const REQUIRED_DIRECTIVES = ["connect-src 'self'", "script-src 'self'", "worker-src 'none'"];

/**
 * 워커를 만들기 전에 CSP 헤더가 실제로 왔는지 본다.
 *
 * 헤더는 서버가 붙인다. 설정이 빠지면 **조용히 방벽이 사라지고** 워커는 평소처럼 돈다.
 * 그 조용함이 이 방식의 유일한 약점이라 여기서 시끄럽게 만든다.
 *
 * 워커 안에서 재 보려던 판은 접었다. 브라우저는 CSP 로 막힌 워커 생성을 동기 예외로
 * 던지지 않고 비동기 `error` 이벤트로 알린다 — 실제 앱에서 확인했다.
 */
export async function assertWorkerCsp(
  url: string = WORKER_URL,
  fetchImpl: typeof fetch = fetch,
): Promise<void> {
  const response = await fetchImpl(url, { method: 'HEAD' });
  const policy = response.headers.get('content-security-policy') ?? '';
  const missing = REQUIRED_DIRECTIVES.filter((directive) => !policy.includes(directive));
  if (missing.length === 0) return;
  throw new Error(
    `${url} 응답의 Content-Security-Policy 에 ${missing.join(', ')} 가 없다. `
    + `웹서버가 이 헤더를 붙여야 워커가 네트워크에서 떨어진다: ${WORKER_CSP}`,
  );
}

/** 헤더를 확인한 뒤에만 워커를 만든다. */
export async function createSandbox(url: string = WORKER_URL, timeoutMs?: number): Promise<Sandbox> {
  await assertWorkerCsp(url);
  return new Sandbox(new Worker(url), timeoutMs);
}

export class Sandbox {
  private readonly pending = new Map<number, Pending>();

  private next = 1;

  constructor(private readonly worker: Worker, private readonly timeoutMs = 5000) {
    this.worker.onmessage = (event: MessageEvent) => {
      const { id, ok, value, error } = event.data as
        { id: number; ok: boolean; value?: unknown; error?: string };
      const waiting = this.pending.get(id);
      if (!waiting) return;
      this.pending.delete(id);
      if (ok) waiting.resolve(value);
      else waiting.reject(new Error(error ?? '알 수 없는 실패'));
    };
  }

  /**
   * 파이썬을 미리 띄운다. 부팅은 몇 초가 걸리고 계산은 그렇지 않다 — 한 예산에
   * 묶으면 첫 계산이 늘 시간 초과로 죽는다. 넉넉히 기다리되 무한정은 아니다.
   */
  warmup(timeoutMs = 60000): Promise<unknown> {
    return this.send({ warmup: true }, timeoutMs);
  }

  run(code: string, data: unknown): Promise<unknown> {
    return this.send({ code, data }, this.timeoutMs);
  }

  private send(message: Record<string, unknown>, timeoutMs: number): Promise<unknown> {
    const id = this.next;
    this.next += 1;
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        if (!this.pending.delete(id)) return;
        // 무한루프는 메시지로 멈출 수 없다. 워커를 죽이는 것이 유일한 방법이다.
        this.worker.terminate();
        reject(new Error(`시간 초과: 계산이 ${timeoutMs}ms 안에 끝나지 않았다`));
      }, timeoutMs);
      this.pending.set(id, {
        resolve: (value) => { clearTimeout(timer); resolve(value); },
        reject: (error) => { clearTimeout(timer); reject(error); },
      });
      this.worker.postMessage({ id, ...message });
    });
  }

  close(): void {
    this.worker.terminate();
    this.pending.clear();
  }
}
